// components/PrimaryAdmissionForm.jsx
import React, { useState } from 'react';
import {
  Container, Paper, Typography, Box, Grid, TextField, Button,
  FormControl, InputLabel, Select, MenuItem, Stepper, Step,
  StepLabel, Alert, Snackbar, CircularProgress, Chip,
  RadioGroup, FormControlLabel, Radio, Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { School, Person, Phone, Email, Home, Assignment } from '@mui/icons-material';
import api from '../services/api';

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 24,
  padding: 32,
  boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
}));

const steps = [
  'Student Information',
  'Parent/Guardian Information',
  'Next of Kin',
  'Review & Submit'
];

const PrimaryAdmissionForm = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [formData, setFormData] = useState({
    // Student info
    full_name: '',
    date_of_birth: '',
    gender: '',
    nationality: 'Tanzanian',
    religion: '',
    birth_certificate_number: '',
    previous_school: '',
    previous_class: '',
    current_class: '',
    class_stream: '',
    residential_street: '',
    residential_distance_km: '',
    disability: '',
    
    // Father info
    father_name: '',
    father_phone: '',
    father_email: '',
    father_occupation: '',
    father_work_place: '',
    
    // Mother info
    mother_name: '',
    mother_phone: '',
    mother_email: '',
    mother_occupation: '',
    mother_work_place: '',
    
    // Next of kin male
    male_next_kin_name: '',
    male_next_kin_phone: '',
    male_next_kin_email: '',
    male_next_kin_occupation: '',
    
    // Next of kin female
    female_next_kin_name: '',
    female_next_kin_phone: '',
    female_next_kin_email: '',
    female_next_kin_occupation: '',
    
    home_languages: '',
    special_needs: '',
    student_type: 'Day'
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await api.post('/primary/admissions', formData);
      setRegistrationNumber(response.data.registration_number);
      setActiveStep(steps.length);
      setSnackbar({
        open: true,
        message: `Application submitted! Registration: ${response.data.registration_number}`,
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Submission failed',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch(step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ color: '#1a237e', mb: 2 }}>
                <Person sx={{ mr: 1 }} /> Student Details
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Full Name" required value={formData.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth type="date" label="Date of Birth" InputLabelProps={{ shrink: true }}
                value={formData.date_of_birth} onChange={(e) => handleChange('date_of_birth', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Gender</InputLabel>
                <Select value={formData.gender} onChange={(e) => handleChange('gender', e.target.value)}>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Applying for Class</InputLabel>
                <Select value={formData.current_class} onChange={(e) => handleChange('current_class', e.target.value)}>
                  <MenuItem value="Pre-school">Pre-school</MenuItem>
                  <MenuItem value="STD I">STD I</MenuItem>
                  <MenuItem value="STD II">STD II</MenuItem>
                  <MenuItem value="STD III">STD III</MenuItem>
                  <MenuItem value="STD IV">STD IV</MenuItem>
                  <MenuItem value="STD V">STD V</MenuItem>
                  <MenuItem value="STD VI">STD VI</MenuItem>
                  <MenuItem value="STD VII">STD VII</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Student Type</InputLabel>
                <Select value={formData.student_type} onChange={(e) => handleChange('student_type', e.target.value)}>
                  <MenuItem value="Day">Day Scholar</MenuItem>
                  <MenuItem value="Boarding">Boarding</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Previous School" value={formData.previous_school}
                onChange={(e) => handleChange('previous_school', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Residential Address" value={formData.residential_street}
                onChange={(e) => handleChange('residential_street', e.target.value)} />
            </Grid>
          </Grid>
        );
      
      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ color: '#1a237e', mb: 2 }}>
                <Person sx={{ mr: 1 }} /> Father/Guardian Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Father's Full Name" value={formData.father_name}
                onChange={(e) => handleChange('father_name', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone Number" value={formData.father_phone}
                onChange={(e) => handleChange('father_phone', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" type="email" value={formData.father_email}
                onChange={(e) => handleChange('father_email', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Occupation" value={formData.father_occupation}
                onChange={(e) => handleChange('father_occupation', e.target.value)} />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ color: '#1a237e', mb: 2, mt: 2 }}>
                <Person sx={{ mr: 1 }} /> Mother/Guardian Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Mother's Full Name" value={formData.mother_name}
                onChange={(e) => handleChange('mother_name', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone Number" value={formData.mother_phone}
                onChange={(e) => handleChange('mother_phone', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" type="email" value={formData.mother_email}
                onChange={(e) => handleChange('mother_email', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Occupation" value={formData.mother_occupation}
                onChange={(e) => handleChange('mother_occupation', e.target.value)} />
            </Grid>
          </Grid>
        );
      
      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ color: '#1a237e', mb: 2 }}>
                Male Next of Kin (Emergency Contact)
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Full Name" value={formData.male_next_kin_name}
                onChange={(e) => handleChange('male_next_kin_name', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone Number" value={formData.male_next_kin_phone}
                onChange={(e) => handleChange('male_next_kin_phone', e.target.value)} />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ color: '#1a237e', mb: 2, mt: 2 }}>
                Female Next of Kin (Emergency Contact)
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Full Name" value={formData.female_next_kin_name}
                onChange={(e) => handleChange('female_next_kin_name', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone Number" value={formData.female_next_kin_phone}
                onChange={(e) => handleChange('female_next_kin_phone', e.target.value)} />
            </Grid>
            
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Languages spoken at home"
                value={formData.home_languages} onChange={(e) => handleChange('home_languages', e.target.value)} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Any special needs or medical conditions"
                value={formData.special_needs} onChange={(e) => handleChange('special_needs', e.target.value)} />
            </Grid>
          </Grid>
        );
      
      case 3:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Please review the information before submitting. Ensure all details are correct.
            </Alert>
            <Paper sx={{ p: 3, bgcolor: '#f5f5f5' }}>
              <Typography variant="h6">Student: {formData.full_name}</Typography>
              <Typography>Class: {formData.current_class} ({formData.student_type})</Typography>
              <Typography>Father: {formData.father_name} | Phone: {formData.father_phone}</Typography>
              <Typography>Mother: {formData.mother_name} | Phone: {formData.mother_phone}</Typography>
            </Paper>
          </Box>
        );
      
      default:
        return null;
    }
  };

  if (activeStep === steps.length) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <StyledPaper sx={{ textAlign: 'center' }}>
          <School sx={{ fontSize: 80, color: '#ff6f00', mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ color: '#1a237e', fontWeight: 700 }}>
            Application Submitted!
          </Typography>
          <Paper sx={{ p: 3, bgcolor: '#fff3e0', my: 3 }}>
            <Typography variant="h6">Student Registration Number:</Typography>
            <Typography variant="h3" sx={{ color: '#ff6f00', fontWeight: 800 }}>
              {registrationNumber}
            </Typography>
          </Paper>
          <Typography variant="body1" paragraph>
            Please save this registration number to track your application status.
          </Typography>
          <Button variant="contained" onClick={() => window.location.href = '/primary/results'}>
            Track Results
          </Button>
        </StyledPaper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <StyledPaper>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              overflow: 'hidden',
              border: '3px solid #166534',
              bgcolor: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 1.5,
              boxShadow: '0 4px 12px rgba(22, 101, 52, 0.25)',
            }}
          >
            <Box
              component="img"
              src="/images/kaozya-english-medium-header.jpg"
              alt="Kaozya English Medium Logo"
              sx={{
                width: '380%',
                height: 'auto',
                objectFit: 'cover',
                objectPosition: 'left center',
                transform: 'scale(1.2) translateX(36%)',
              }}
            />
          </Box>
          <Typography variant="h4" align="center" sx={{ color: '#166534', fontWeight: 800, mb: 0.5, letterSpacing: '0.5px' }}>
            KAOZYA ENGLISH MEDIUM SCHOOL
          </Typography>
          <Typography variant="body2" align="center" sx={{ color: '#1a237e', fontWeight: 600, mb: 0.5 }}>
            P.O. BOX 188, KALENGAKELU, MLIMBA-MOROGORO • REG NO: EM.21666
          </Typography>
          <Typography variant="subtitle2" align="center" sx={{ color: '#555', fontStyle: 'italic', mb: 1 }}>
            "QUALITY EDUCATION FOR A BETTER FUTURE"
          </Typography>
          <Chip label={`Fomu ya Kujiunga (Admission Form) ${new Date().getFullYear()}/${new Date().getFullYear() + 1}`} color="success" sx={{ fontWeight: 700 }} />
        </Box>
        
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {getStepContent(activeStep)}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button disabled={activeStep === 0} onClick={() => setActiveStep(prev => prev - 1)}>
            Back
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button variant="contained" onClick={handleSubmit} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Submit Application'}
            </Button>
          ) : (
            <Button variant="contained" onClick={() => setActiveStep(prev => prev + 1)}>
              Next
            </Button>
          )}
        </Box>
      </StyledPaper>
      
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default PrimaryAdmissionForm;